<script setup>
import {ref, computed, onMounted} from 'vue';
import { useNotificationStore } from '@/stores/NotificationStore.js';

const props = defineProps({
    course: {
        type: Object,
        required: true
    }
});

const notificationStore = useNotificationStore();
const participants = ref([]);
const chapters = ref([]);
const loading = ref(true);
const error = ref(null);
const searchQuery = ref('');
const sortField = ref('name');
const sortDirection = ref('asc');

const filteredParticipants = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();

    const filtered = query
        ? participants.value.filter(participant =>
            participant.user.name.toLowerCase().includes(query) ||
            participant.user.email.toLowerCase().includes(query)
        )
        : [...participants.value];

    return filtered.sort((participantA, participantB) => {
        const valueA = sortField.value === 'licenseCreatedAt'
            ? (participantA.license?.created_at ?? '')
            : participantA.user.name.toLowerCase();
        const valueB = sortField.value === 'licenseCreatedAt'
            ? (participantB.license?.created_at ?? '')
            : participantB.user.name.toLowerCase();

        if (valueA < valueB) {
            return sortDirection.value === 'asc' ? -1 : 1;
        }

        if (valueA > valueB) {
            return sortDirection.value === 'asc' ? 1 : -1;
        }

        return participantA.user.name.localeCompare(participantB.user.name);
    });
});

const setSort = (field) => {
    if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
        return;
    }

    sortField.value = field;
    sortDirection.value = field === 'licenseCreatedAt' ? 'desc' : 'asc';
};

const getSortIconClass = (field) => {
    if (sortField.value !== field) {
        return 'fa-solid fa-sort text-gray-400';
    }

    return sortDirection.value === 'asc'
        ? 'fa-solid fa-sort-up text-wlPrimary'
        : 'fa-solid fa-sort-down text-wlPrimary';
};

const revokeLicense = async (participant) => {
    if (!participant.user.lead_id) {
        return;
    }

    const response = await axios.delete(route('crm.leads.license.delete', {
        id: participant.user.lead_id,
        type: 'course',
        license_id: participant.license.id,
    }));

    if (response.data.success) {
        notificationStore.addNotification({
            type: 'success',
            title: _mft('crm:licenses.revoked'),
            message: participant.user.name,
            dismissAfter: 5000,
        });

        await loadParticipants();
    }
};

const loadParticipants = async () => {
    loading.value = true;
    error.value = null;

    try {
        const response = await axios.get(route('api.course.backoffice.participants', props.course.id));
        participants.value = response.data.participants;
        chapters.value = response.data.chapters;
    } catch (e) {
        error.value = _mft('shared:error.generic');
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    loadParticipants();
});
</script>

<template>
    <div>
        <div v-if="loading" class="p-8 text-center">
            <mf-spinner-medium class="w-8 h-8 mx-auto" />
        </div>

        <div v-else-if="error" class="p-8 text-center text-red-600">
            {{ error }}
        </div>

        <div v-else-if="participants.length === 0" class="p-8 text-center text-gray-500">
            {{ _mft('course:participants.empty') }}
        </div>

        <div v-else>
            <div class="mb-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div class="w-full md:max-w-md">
                    <input-text
                        v-model:content="searchQuery"
                        :placeholder="_mft('shared:search')"
                        class="!mb-0"
                    />
                </div>
                <div class="flex flex-wrap items-center gap-2">
                    <button-base
                        :style-type="sortField === 'name' ? 'tinted' : 'secondary'"
                        @click="setSort('name')"
                    >
                        <template #icon>
                            <i :class="getSortIconClass('name')"></i>
                        </template>
                        {{ _mft('shared:name') }}
                    </button-base>
                    <button-base
                        :style-type="sortField === 'licenseCreatedAt' ? 'tinted' : 'secondary'"
                        @click="setSort('licenseCreatedAt')"
                    >
                        <template #icon>
                            <i :class="getSortIconClass('licenseCreatedAt')"></i>
                        </template>
                        {{ _mft('shared:date.createdAt') }}
                    </button-base>
                </div>
            </div>
            <div class="overflow-x-auto pt-4">
                <ul v-if="course.type === 'feed'" class="divide-y">
                    <li
                        v-for="(participant, i) in filteredParticipants"
                        :key="i"
                        class="py-1"
                    >
                        <div class="flex items-start justify-between gap-3 rounded p-2">
                            <div class="min-w-0 flex-1">
                                <component
                                    :is="participant.user.lead_id ? 'a' : 'span'"
                                    :href="route('crm.leads.view', participant.user.lead_id)"
                                    target="_blank"
                                    class="text-sm font-medium text-gray-900 truncate"
                                    :class="participant.user.lead_id ? 'mf-link' : ''"
                                >
                                    {{ participant.user.name }}
                                </component>
                                <div class="text-sm text-gray-500 truncate">
                                    {{ participant.user.email }}
                                </div>
                                <div class="text-xs text-gray-400 truncate">
                                    {{ _mft('course:license.createdAt') }}: {{ participant.license.created_at || '-' }}
                                </div>
                            </div>
                            <button-with-confirmation
                                v-if="participant.user.lead_id"
                                style-type="danger"
                                size="sm"
                                @confirmed="revokeLicense(participant)"
                                :confirm-text="_mft('shared:confirm.cannotBeUndone')"
                                confirm-type="danger"
                                :confirm-button-label="_mft('shared:action.delete')"
                                :abort-button-label="_mft('shared:modal.defaultCancel')"
                            >
                                <span class="whitespace-nowrap">
                                    {{ _mft('crm:licenses.revoke') }}
                                </span>
                            </button-with-confirmation>
                        </div>
                    </li>
                </ul>

                <table
                    v-if="course.type === 'class'"
                    class="min-w-full divide-y divide-gray-200"
                >
                    <thead>
                        <tr>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {{ _mft('shared:name') }}
                            </th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                {{ _mft('course:license.createdAt') }}
                            </th>
                            <th scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {{ _mft('course:progress') }}
                            </th>
                            <th
                                v-for="chapter in chapters"
                                :key="chapter.id"
                                scope="col"
                                class="px-2 py-3 text-center text-xs font-medium text-gray-500 tracking-wider relative z-50"
                                :title="chapter.title"
                            >
                                <container-with-tooltip
                                    alignment="right"
                                    vertical-alignment="bottom"
                                    class="relative"
                                >
                                    <span class="block max-w-[100px] truncate uppercase cursor-default">{{ chapter.title }}</span>
                                    <template #tooltip>
                                        <div class="max-w-md min-w-36 relative z-50">
                                            {{ chapter.title }}
                                        </div>
                                    </template>
                                </container-with-tooltip>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="participant in filteredParticipants" :key="participant.license.id">
                            <td class="px-4 py-3 whitespace-nowrap sticky left-0 bg-white max-w-48 z-10">
                                <a
                                    v-if="participant.user.lead_id"
                                    :href="route('crm.leads.view', participant.user.lead_id)"
                                    target="_blank"
                                    class="block hover:bg-gray-50 -m-2 p-2 rounded"
                                >
                                    <div class="text-sm font-medium text-gray-900 truncate">
                                        {{ participant.user.name }}
                                    </div>
                                    <div class="text-sm text-gray-500 truncate">
                                        {{ participant.user.email }}
                                    </div>
                                </a>
                                <template v-else>
                                    <container-with-tooltip>
                                        <div class="text-sm font-medium text-gray-900 truncate cursor-default">
                                            {{ participant.user.name }}
                                        </div>
                                        <template #tooltip>
                                            {{ participant.user.name }}
                                        </template>
                                    </container-with-tooltip>
                                    <container-with-tooltip>
                                        <div class="text-sm text-gray-500 truncate cursor-default">
                                            {{ participant.user.email }}
                                        </div>
                                        <template #tooltip>
                                            {{ participant.user.email }}
                                        </template>
                                    </container-with-tooltip>
                                </template>
                            </td>
                            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                <div class="flex flex-col items-start gap-0.5">
                                    <span class="text-xs">{{ participant.license.created_at || '-' }}</span>
                                    <button-with-confirmation
                                        v-if="participant.user.lead_id"
                                        style-type="danger"
                                        size="xs"
                                        @confirmed="revokeLicense(participant)"
                                        :confirm-text="_mft('shared:confirm.cannotBeUndone')"
                                        confirm-type="danger"
                                        :confirm-button-label="_mft('shared:action.delete')"
                                        :abort-button-label="_mft('shared:modal.defaultCancel')"
                                        :align="'left'"
                                        class="z-0"
                                        modal-class="absolute -top-2 left-0 p-2 bg-white rounded shadow-md text-black z-modal"
                                    >
                                        <span class="whitespace-nowrap">
                                            {{ _mft('crm:licenses.revoke') }}
                                        </span>
                                    </button-with-confirmation>
                                </div>
                            </td>
                            <td class="px-4 py-3 whitespace-nowrap text-right">
                                <div class="flex items-center justify-end space-x-2">
                                    <div class="w-24 bg-gray-200 rounded-full h-2">
                                        <div
                                            class="h-2 rounded-full transition-all duration-300"
                                            :class="participant.summary.completion_percentage === 100 ? 'bg-myflowGreen' : 'bg-wlPrimary'"
                                            :style="{ width: participant.summary.completion_percentage + '%' }"
                                        ></div>
                                    </div>
                                    <span class="text-sm text-gray-600 w-12 text-right">
                                        {{ participant.summary.completion_percentage }}%
                                    </span>
                                </div>
                                <div class="text-xs text-gray-500 mt-1">
                                    {{ participant.summary.chapters_completed }}/{{ participant.summary.chapters_total }}
                                </div>
                            </td>

                            <td
                                v-for="chapterProgress in participant.chapter_progress"
                                :key="chapterProgress.chapter_id"
                                class="px-2 py-3 text-center"
                            >
                                <span
                                    v-if="chapterProgress.completed"
                                    class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-myflowGreen-100 text-myflowGreen-600"
                                    :title="_mft('course:participants.chapterCompleted')"
                                >
                                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                    </svg>
                                </span>
                                <span
                                    v-else-if="chapterProgress.progress > 0"
                                    class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 text-xs font-medium"
                                    :title="_mft('course:participants.chapterInProgress')"
                                >
                                    {{ Math.round(chapterProgress.progress * 100) }}%
                                </span>
                                <span
                                    v-else
                                    class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-400"
                                    :title="_mft('course:participants.chapterNotStarted')"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" r="10" stroke-width="2" />
                                    </svg>
                                </span>
                            </td>
                        </tr>
                    <tr v-if="filteredParticipants.length === 0">
                            <td :colspan="chapters.length + 3" class="px-4 py-8 text-center text-gray-500">
                                {{ _mft('course:participants.noSearchResults') }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
